import React, { useMemo } from 'react';

import DeleteObserver from 'src/pages/observers/ListObserversPage/components/DeleteObserver';
import { useGetAllPeriodicitiesQuery, useGetObserversQuery, useGetSeveritiesQuery } from 'src/redux/services/centralApi';
import { AppstoreOutlined, EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { isQueryGroup, type Observer } from 'src/interfaces/entities/observer';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Flex, Space, Table, Tag, Tooltip, type TableColumnsType } from 'antd';
import EditObserver from 'src/pages/observers/ListObserversPage/components/EditObserver';
import ObserverHistory from 'src/components/ObserverHistory';
import ForceExecution from 'src/pages/observers/ListObserversPage/components/ForceExecution';
import DuplicateObserver from 'src/pages/observers/ListObserversPage/components/DuplicateObserver';
import VisualizeObserver from 'src/pages/observers/ListObserversPage/components/VisualizeObserver';
import dayjs from 'dayjs';

const isOnlyNotify = (record: Observer) =>
	isQueryGroup(record) || (Array.isArray(record.flags) && record.flags.includes('only_notify'));

export default function ListObserverPage() {
	const navigate = useNavigate();
	const {
		data: observersData,
		isLoading: isLoadingObservers,
		isFetching: isFetchingObservers,
		refetch: refetchObservers,
	} = useGetObserversQuery();

	const {
		data: periodicitiesData,
		isLoading: isLoadingPeriodicities,
	} = useGetAllPeriodicitiesQuery();

	const {
		data: severitiesData,
		isLoading: isLoadingSeverities,
	} = useGetSeveritiesQuery();

	const columns = useMemo<TableColumnsType<Observer>>(
		() => [
			{
				title: 'Tipo',
				key: 'type',
				width: 170,
				filters: [
					{ text: 'Observador', value: 'observer', },
					{ text: 'Grupo de consultas', value: 'group', },
				],
				onFilter: (value, record) => (isQueryGroup(record) ? 'group' : 'observer') === value,
				render: (_, record) => (
					isQueryGroup(record)
						? <Tooltip title={`${record.report?.queries?.length} consultas, sem condição de alerta: sempre envia no horário`}>
							<Tag color='blue' icon={<AppstoreOutlined />}>Grupo de consultas</Tag>
						</Tooltip>
						: <Tooltip title='Notifica e gera ocorrência quando a condição de alerta é atingida'>
							<Tag icon={<EyeOutlined />}>Observador</Tag>
						</Tooltip>
				),
			},
			{
				dataIndex: 'name',
				title: 'Nome',
				key: 'name',
				filters: [
					{ text: 'Só notificação', value: true, },
					{ text: 'Gera ocorrência', value: false, },
				],
				onFilter: (value, record) => isOnlyNotify(record) === value,
				render: (value: string, record) => {
					const conditions = record.conditions;
					let cellValue = value;

					if (cellValue && conditions && cellValue.includes('{{')) {
						Object.entries(conditions).forEach(([ string, condition, ]) => {
							if (condition.type !== 'sql') {
								const conditionValue: string = condition.value?.toString() ?? '';
								cellValue = cellValue.replace(`{{${string}}}`, conditionValue);
							}
						});
					}

					const content = cellValue && cellValue.length > 50
						? <div title={cellValue}>{cellValue.substring(0, 47) + '...'}</div>
						: cellValue;

					if (!isOnlyNotify(record)) return content;

					// Não gera ocorrência: grupo de consultas ou observador com "notificação sem ocorrência"
					return (
						<Badge.Ribbon text='Só notificação' color='purple' style={{ top: 4, }}>
							<div style={{ minHeight: 30, paddingRight: 112, display: 'flex', alignItems: 'center', }}>
								{content}
							</div>
						</Badge.Ribbon>
					);
				},
			},
			{
				dataIndex: 'custom_preview_message',
				title: 'Mensagem de alerta',
				key: 'custom_preview_message',
				render: (value: string) => {
					if (value && value.length > 50) {
						return <div title={value}>{value.substring(0, 47) + '...'}</div>;
					}
					return value;
				},
			},
			{
				dataIndex: 'periodicity_id',
				title: 'Periodicidade',
				key: 'periodicity_id',
				render: (value: string) => {
					if (isLoadingPeriodicities) return 'Carregando...';
					const periodicity = periodicitiesData?.find((el) => el.periodicity_id === value);
					return periodicity?.alias;
				},
			},
			{
				dataIndex: 'severity_id',
				title: 'Severidade',
				key: 'severity_id',
				render: (value: string | null) => {
					if (!value) return '-';
					if (isLoadingSeverities) return 'Carregando...';
					const severity = severitiesData?.find((el) => el.severity_id === value);
					return (
						<Flex gap={4} align='center' style={{ height: '100%', }}>
							<div
								style={{
									borderRadius: '6px',
									height: '16px',
									width: '8px',
									backgroundColor: severity?.color,
								}}
							/>
							{severity?.name}
						</Flex>
					);
				},
			},
			{
				dataIndex: 'is_active',
				title: 'Ativo',
				key: 'is_active',
				width: 80,
				filters: [
					{ text: 'Ativo', value: true, },
					{ text: 'Inativo', value: false, },
				],
				onFilter: (value, record) => record.is_active === value,
				render: (value: boolean) => value ? 'Ativo' : 'Inativo',
			},
			{
				dataIndex: 'creation_date',
				title: 'Data de criação',
				key: 'creation_date',
				render: (value: string) => value ? dayjs(value).format('DD/MM/YYYY') : '-',
				sorter: (a, b) => {
					const dateA = a.creation_date ? dayjs(a.creation_date) : null;
					const dateB = b.creation_date ? dayjs(b.creation_date) : null;
					if (dateA && dateB && dateA.isValid() && dateB.isValid()) return dateA.diff(dateB);
					if (!dateA && !dateB) return 0;
					if (!dateA) return 1;
					if (!dateB) return -1;
					return 0;
				},
			},
			{
				dataIndex: 'last_execution',
				title: 'Última execução',
				key: 'last_execution',
				render: (value: string) => value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-',
				sorter: (a, b) => {
					const dateA = a.last_execution ? dayjs(a.last_execution) : null;
					const dateB = b.last_execution ? dayjs(b.last_execution) : null;
					if (dateA && dateB && dateA.isValid() && dateB.isValid()) return dateA.diff(dateB);
					if (!dateA && !dateB) return 0;
					if (!dateA) return 1;
					if (!dateB) return -1;
					return 0;
				},
			},
			{
				title: 'Ações',
				key: 'actions',
				render: (_, record) => (
					isQueryGroup(record)
						// Grupo de consultas: edição em página própria, sem SQL único nem histórico de ocorrências
						? <Space>
							<DeleteObserver row={record} />
							<Tooltip title='Editar'>
								<Button
									type='text'
									icon={<EditOutlined />}
									onClick={() => navigate(`/observador/grupo/adicionar?id=${record.observer_id}`)}
								/>
							</Tooltip>
							<ForceExecution row={record} />
						</Space>
						: <Space>
							<DeleteObserver row={record} />
							<EditObserver row={record} />
							<DuplicateObserver row={record} />
							<VisualizeObserver row={record} />
							<ForceExecution row={record} />
							<ObserverHistory observer={record} forMantine />
						</Space>
				),
			},
		],
		[ periodicitiesData, severitiesData, isLoadingPeriodicities, isLoadingSeverities, ]
	);

	return (
		<DashboardTemplate pageTitle="Gerenciar Observadores">
			<Flex style={{ padding: 5, marginBottom: 8, }} gap={8} justify="end">
				<Button
					type="primary"
					onClick={() => navigate('/observador/adicionar')}
					icon={<PlusOutlined />}
				>
Adicionar
				</Button>
				<Button
					type="text"
					onClick={refetchObservers}
					icon={<ReloadOutlined />}
				/>
			</Flex>
			<Table<Observer>
				columns={columns}
				dataSource={observersData ?? []}
				rowKey="observer_id"
				loading={isLoadingObservers || isFetchingObservers}
				size="small"
			/>
		</DashboardTemplate>
	);
}
